import { useState } from 'react';

import { Loader2, Repeat } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { EditList, PromptDiff } from '@/components/PromptDiff';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { useConfigBranches } from '@/hooks/useConfigRepository';
import { usePageTitle } from '@/hooks/usePageTitle';
import {
  useCancelPipelineRun,
  useDiscardPipelineRun,
  usePipelineRun,
  usePromotePipelineRun,
} from '@/hooks/usePipelineRuns';
import { extractErrorMessage } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';

import { ScenarioGateBadge } from './components/ScenarioGateBadge';
import { ReportTab } from './ReportTab';
import { StageTimeline } from './StageTimeline';

import type { ConfigPatch } from '@/lib/api/pipeline-runs.api';

// ── Patch rendering helpers ──────────────────────────────────────────────────

/** Render a single root cause's ConfigPatch: edit lists per text field + fieldPatch table. */
const PatchDetail = ({ patch }: { patch: ConfigPatch }) => (
  <div className="mt-2 space-y-3">
    {patch.textEdits.map((te) => (
      <div key={te.field}>
        <p className="text-muted-foreground mb-1 text-xs font-medium">{te.field}</p>
        <EditList edits={te.edits} append={te.append} />
      </div>
    ))}
    {patch.fieldPatch && Object.keys(patch.fieldPatch).length > 0 && (
      <div>
        <p className="text-muted-foreground mb-1 text-xs font-medium">Field overrides</p>
        <div className="overflow-x-auto rounded border">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-muted/50 border-b">
                <th className="px-3 py-1.5 text-left font-medium">Field</th>
                <th className="px-3 py-1.5 text-left font-medium">Value</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(patch.fieldPatch).map(([key, value]) => (
                <tr key={key} className="border-b last:border-0">
                  <td className="px-3 py-1.5 font-mono">{key}</td>
                  <td className="px-3 py-1.5">
                    <code className="break-words whitespace-pre-wrap">
                      {typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
                    </code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}
    {patch.rationale && <p className="text-muted-foreground text-xs italic">{patch.rationale}</p>}
  </div>
);

// ── Main page ────────────────────────────────────────────────────────────────

type RunTab = 'details' | 'report';

export const PipelineRunDetailPage = () => {
  usePageTitle('Pipeline Run');
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const { data: run, isLoading } = usePipelineRun(id!);
  const cancelMutation = useCancelPipelineRun();
  const promoteMutation = usePromotePipelineRun();
  const discardMutation = useDiscardPipelineRun();
  const [activeTab, setActiveTab] = useState<RunTab>('details');

  // The gate is evaluated on the candidate branch head. The branch list is admin-only, so
  // non-admins fall back to the gate without a commit id (backend uses the branch head).
  const isAdmin = useAuthStore((state) => state.user?.role === 'admin');
  const { data: branches } = useConfigBranches(
    isAdmin && run?.branch_id ? run.configuration_id : undefined,
  );
  const candidateHeadCommitId = branches?.find((b) => b.id === run?.branch_id)?.head_commit_id;

  const handlePromote = () => {
    promoteMutation.mutate(id!, {
      onSuccess: () => toast({ title: 'Promoted', description: 'Candidate is now live.' }),
      onError: (err) =>
        toast({
          variant: 'destructive',
          title: 'Promote refused',
          description: extractErrorMessage(err),
        }),
    });
  };

  if (isLoading || !run) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
      </div>
    );
  }

  const progress = run.progress;
  const isRunning = run.status === 'RUNNING' || run.status === 'QUEUED';
  const canPromote =
    (run.status === 'SUCCEEDED' || run.status === 'PARTIAL') &&
    run.root_causes?.length > 0 &&
    !run.promoted_commit_id;
  const summary = run.summary;
  const candidateDiff = run.candidate_diff;
  const rejectedFindings = run.findings.filter((f) => f.verdict === 'rejected');

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">Pipeline run</p>
          <h1 className="text-2xl font-bold tracking-tight">{run.id.slice(0, 8)}</h1>
        </div>
        <div className="flex gap-2">
          {isRunning && (
            <Button
              variant="destructive"
              size="sm"
              disabled={cancelMutation.isPending}
              onClick={() => cancelMutation.mutate(id!)}
            >
              {cancelMutation.isPending ? 'Cancelling...' : 'Cancel'}
            </Button>
          )}
          {canPromote && (
            <ScenarioGateBadge
              configurationId={run.configuration_id}
              commitId={candidateHeadCommitId}
              className="self-center"
            />
          )}
          {canPromote && (
            <Button size="sm" disabled={promoteMutation.isPending} onClick={handlePromote}>
              {promoteMutation.isPending ? 'Promoting...' : 'Promote'}
            </Button>
          )}
          {canPromote && (
            <Button
              variant="outline"
              size="sm"
              disabled={discardMutation.isPending}
              onClick={() => discardMutation.mutate(id!)}
            >
              {discardMutation.isPending ? 'Discarding...' : 'Discard'}
            </Button>
          )}
          <Button variant="outline" size="sm" asChild>
            <Link to="/agent-improvement/runs">Back to runs</Link>
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-4">
          <StageTimeline
            currentStage={run.stage}
            status={run.status}
            progress={progress}
            configLocked={summary.configLocked === true}
          />
          {progress && isRunning && (
            <div className="text-muted-foreground mt-3 text-sm">{progress.message}</div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Findings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{run.findings.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Root Causes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{run.root_causes.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Test Cases</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{run.test_cases.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Decision</CardTitle>
          </CardHeader>
          <CardContent>
            <Badge>{String(summary.decision ?? run.status)}</Badge>
          </CardContent>
        </Card>
      </div>

      <div className="flex gap-4 border-b" role="tablist">
        {(['details', 'report'] as const).map((tab) => (
          <button
            key={tab}
            role="tab"
            aria-selected={activeTab === tab}
            onClick={() => setActiveTab(tab)}
            className={`border-b-2 px-1 pb-2 text-sm font-medium capitalize transition-colors ${
              activeTab === tab
                ? 'border-primary text-foreground'
                : 'text-muted-foreground hover:text-foreground border-transparent'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === 'report' && <ReportTab runId={id!} />}

      {activeTab === 'details' && run.findings.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Findings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {run.findings.map((f) => (
                <div key={f.id} className="flex items-center gap-2 border-b pb-2 text-sm">
                  <Badge variant={f.verdict === 'confirmed' ? 'destructive' : 'secondary'}>
                    {f.verdict ?? 'pending'}
                  </Badge>
                  {f.call_id ? (
                    <Link
                      to={`/calls/${f.call_id}`}
                      className="text-primary font-mono text-xs hover:underline"
                    >
                      {f.call_id.slice(0, 8)}
                    </Link>
                  ) : (
                    <span className="text-muted-foreground font-mono text-xs">scenario</span>
                  )}
                  <span className="text-muted-foreground text-xs">{f.assessment_name}</span>
                  <span className="text-muted-foreground truncate text-xs">
                    {f.verdict_reasoning?.slice(0, 80)}
                  </span>
                </div>
              ))}
            </div>
            {rejectedFindings.length > 0 && (
              <div className="mt-3 flex items-center gap-2 border-t pt-3">
                <span className="text-muted-foreground text-xs">
                  {rejectedFindings.length} rejected
                </span>
                <Button variant="outline" size="sm" asChild>
                  <Link
                    to={`/agent-improvement/judge-tuning/new?calls=${[...new Set(rejectedFindings.map((f) => f.call_id).filter(Boolean))].join(',')}`}
                  >
                    <Repeat className="mr-1 h-3 w-3" />
                    Tune judge from this run
                  </Link>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'details' && run.root_causes.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Root Causes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {run.root_causes.map((rc) => (
                <div key={rc.id} className="border-b pb-4 last:border-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{rc.title}</span>
                    <Badge variant="outline">{rc.target_area}</Badge>
                  </div>
                  <p className="text-muted-foreground mt-1 text-sm">{rc.description}</p>
                  {rc.patch && <PatchDetail patch={rc.patch} />}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab === 'details' && candidateDiff && (
        <Card>
          <CardHeader>
            <CardTitle>Candidate Changes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {candidateDiff.textFields.map((tf) => (
              <div key={tf.field}>
                <p className="mb-1 text-sm font-medium">{tf.field}</p>
                <PromptDiff before={tf.before ?? ''} after={tf.after ?? ''} />
              </div>
            ))}
            {candidateDiff.otherFields.length > 0 && (
              <div>
                <p className="mb-1 text-sm font-medium">Other field changes</p>
                <div className="overflow-x-auto rounded border">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-muted/50 border-b">
                        <th className="px-3 py-1.5 text-left font-medium">Field</th>
                        <th className="px-3 py-1.5 text-left font-medium">Before</th>
                        <th className="px-3 py-1.5 text-left font-medium">After</th>
                      </tr>
                    </thead>
                    <tbody>
                      {candidateDiff.otherFields.map((entry) => (
                        <tr key={entry.field} className="border-b last:border-0">
                          <td className="px-3 py-1.5 font-mono">{entry.field}</td>
                          <td className="px-3 py-1.5">
                            <code className="rounded bg-rose-50 px-1 py-0.5 break-words whitespace-pre-wrap text-rose-700 dark:bg-rose-950 dark:text-rose-300">
                              {typeof entry.before === 'string'
                                ? entry.before
                                : JSON.stringify(entry.before, null, 2)}
                            </code>
                          </td>
                          <td className="px-3 py-1.5">
                            <code className="rounded bg-emerald-50 px-1 py-0.5 break-words whitespace-pre-wrap text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
                              {typeof entry.after === 'string'
                                ? entry.after
                                : JSON.stringify(entry.after, null, 2)}
                            </code>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'details' && run.test_cases.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Test Cases</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {run.test_cases.map((tc) => (
                <div key={tc.id} className="flex items-center gap-2 border-b pb-2 text-sm">
                  <Badge
                    variant={
                      tc.result === 'pass'
                        ? 'default'
                        : tc.result === 'fail'
                          ? 'destructive'
                          : 'secondary'
                    }
                  >
                    {tc.result ?? tc.status}
                  </Badge>
                  <span className="text-xs">{tc.kind}</span>
                  <Link
                    to={`/calls/${tc.source_call_id}`}
                    className="text-primary font-mono text-xs hover:underline"
                  >
                    {tc.source_call_id.slice(0, 8)}
                  </Link>
                  {tc.test_call_id && (
                    <Link
                      to={`/calls/${tc.test_call_id}`}
                      className="text-primary font-mono text-xs hover:underline"
                    >
                      test: {tc.test_call_id.slice(0, 8)}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab === 'details' && run.error && (
        <Card>
          <CardHeader>
            <CardTitle className="text-red-500">Error</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="text-sm whitespace-pre-wrap text-red-600">{run.error}</pre>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
